import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import {
  createVirtualClassroomEvent,
  publishVirtualClassroomEvent,
} from 'modules/virtualClassrooms/manager/EventCreator/api/eventCreatorService'
import { EventCreatorFormParams } from 'modules/virtualClassrooms/manager/EventCreator/types'
import eventDetailsRoutes, {
  tabs as eventDetailsTabs,
} from 'modules/virtualClassrooms/manager/EventDetails/routing/routes'
import { EventCreatorState, ApiEventCreateResponse } from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:manager.eventCreator`

const initialState: EventCreatorState = {
  eventCreateRequest: {},
  eventPublishRequest: {},
}

const eventCreatorSlice = createSlice({
  name: 'virtualClassroomManagerEventCreator',
  initialState,
  reducers: {
    eventCreateRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiEventCreateResponse>>
    ) {
      state.eventCreateRequest = action.payload
    },
    eventPublishRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.eventPublishRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { eventCreateRequested, eventPublishRequested } = eventCreatorSlice.actions
export default eventCreatorSlice.reducer

const handleOpenEvent =
  (response: ApiMiddlewarePayload<ApiEventCreateResponse>, params?: { eventId: Guid }): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      const eventId = params?.eventId || result.createVirtualClassroomEvent.eventId
      dispatch(push(`${eventDetailsRoutes.root}/${eventId}/${eventDetailsTabs.eventEditor}`))
    }
    dispatch(eventCreateRequested(response))
  }

const handlePublishEvent =
  (response: ApiMiddlewarePayload<ApiEventCreateResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      dispatch(publishEvent(result.createVirtualClassroomEvent.eventId))
    }
    dispatch(eventCreateRequested(response))
  }

const publishEvent = (eventId: Guid) => {
  return createApiAction({
    apiCalls: {
      publishVirtualClassroomEvent: () => publishVirtualClassroomEvent(eventId),
    },
    payloadAction: eventPublishRequested,
    nextAction: {
      action: handleOpenEvent,
      params: { eventId },
    },
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successCreateAndPublish`),
  })
}

export const createAndPublishEvent = (formData: EventCreatorFormParams) => {
  return createApiAction({
    apiCalls: {
      createVirtualClassroomEvent: () => createVirtualClassroomEvent(formData),
    },
    payloadAction: eventCreateRequested,
    nextAction: handlePublishEvent,
    enableErrorNotification: true,
  })
}

export const saveDraft = (formData: EventCreatorFormParams) => {
  return createApiAction({
    apiCalls: {
      createVirtualClassroomEvent: () => createVirtualClassroomEvent(formData),
    },
    payloadAction: eventCreateRequested,
    nextAction: handleOpenEvent,
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successSaveAsDraft`),
  })
}
